import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';

import { IN_PAGE_SEARCH_PARAM_KEY } from '~/modules/professional/config/constants';

/** How long typing settles before the term is written to the URL. */
const COMMIT_DELAY_MS = 400;

/**
 * An item tab's in-page search term, held in the URL.
 *
 * Every tab's search belongs in the address bar whether or not the tab needs
 * it there to fetch: it is what makes a narrowed list shareable, bookmarkable
 * and able to survive a reload. Videos and Downloads filtered in local state
 * instead, so a reader who found the four wallpaper videos had no way to send
 * anyone to them, and their own reload lost the search.
 *
 * The input stays responsive — the term is local and the URL follows once
 * typing settles — and a committed term moving underneath (Back, Forward, the
 * clear control) pulls the input with it — but not this hook's own echo. On a
 * tab whose loader reads the term, the URL catches up a round trip after the
 * term was sent, by which time the reader has typed more; copying it back
 * would delete what they had just typed. `page` is dropped on every change,
 * because a new term restarts the run of pages.
 */
export const useInPageSearchTerm = (): [string, (next: string) => void] => {
    const [searchParams, setSearchParams] = useSearchParams();
    const committedTerm = searchParams.get(IN_PAGE_SEARCH_PARAM_KEY) ?? '';
    const [term, setTerm] = useState(committedTerm);
    // The last term this hook wrote to the URL, to tell its echo from a move.
    const sentTerm = useRef(committedTerm);

    // Back/forward and a cleared pill both move the committed term; follow it.
    useEffect(() => {
        if (committedTerm === sentTerm.current) return;
        sentTerm.current = committedTerm;
        setTerm(committedTerm);
    }, [committedTerm]);

    useEffect(() => {
        const next = term.trim();
        if (next === committedTerm) return;
        const timer = setTimeout(() => {
            sentTerm.current = next;
            setSearchParams(
                (current) => {
                    const params = new URLSearchParams(current);
                    if (next) {
                        params.set(IN_PAGE_SEARCH_PARAM_KEY, next);
                    } else {
                        params.delete(IN_PAGE_SEARCH_PARAM_KEY);
                    }
                    params.delete('page');
                    return params;
                },
                { replace: true, preventScrollReset: true },
            );
        }, COMMIT_DELAY_MS);
        return () => clearTimeout(timer);
    }, [term, committedTerm, setSearchParams]);

    return [term, setTerm];
};
